import { useQuery } from '@tanstack/react-query';

import { useToast } from '@aslanpay/portal-common-lib';

import { RECONCILIATION_POLL_INTERVAL_MS } from 'app/constants/reconciliations';

import { fetchAdminNames, fetchCompanyNames } from 'app/hooks/useFetchReconciliationsPage';

import SalaryReconciliationsApiHandler from 'app/services/salaryReconciliationsApiHandler';

import {
  FetchSalaryReconciliationsRequest,
  SalaryReconciliationRunsPage,
} from 'app/types/salaryReconciliations';

import { isActiveReconciliationRun } from 'app/utils/reconciliations';

export const SALARY_RECONCILIATIONS_PAGE_QUERY_KEY = 'fetchSalaryReconciliationsPage';

/** Lists salary accrual reconciliation runs, newest first, refreshing while any of them is still queued or running. */
const useFetchSalaryReconciliationsPage = (
  { companyId, status, page, size }: FetchSalaryReconciliationsRequest,
  enabled = true
) => {
  const toast = useToast();

  return useQuery<SalaryReconciliationRunsPage, Error>(
    [SALARY_RECONCILIATIONS_PAGE_QUERY_KEY, companyId, status, page, size],
    async () => {
      const runs = await SalaryReconciliationsApiHandler.listReconciliations({
        companyId,
        status,
        page,
        size,
      });

      const companyIds = [...new Set(runs.list.flatMap((run) => run.companyIds))];
      const adminIds = [
        ...new Set(runs.list.filter((run) => !run.automated).map((run) => run.triggeredBy)),
      ];
      const [companyNames, adminNames] = await Promise.all([
        fetchCompanyNames(companyIds),
        fetchAdminNames(adminIds),
      ]);

      return {
        ...runs,
        list: runs.list.map((run) => ({
          ...run,
          companyNames: run.companyIds
            .map((id) => companyNames.get(id))
            .filter((name): name is string => Boolean(name)),
          triggeredByName: run.automated ? undefined : adminNames.get(run.triggeredBy),
        })),
      };
    },
    {
      enabled,
      refetchInterval: (data) =>
        data?.list.some((run) => isActiveReconciliationRun(run))
          ? RECONCILIATION_POLL_INTERVAL_MS
          : false,
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'Sorry, we failed to fetch the salary reconciliations. Please try again.',
        });
      },
    }
  );
};

export default useFetchSalaryReconciliationsPage;
